import { Dispatch, MouseEvent, SetStateAction, useEffect, useMemo, useState } from "react";
import { FoodsDeleteButton, Typography as FoodsTypography } from "@foodpilot/foods";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Box, CircularProgress, IconButton, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DragDropFileUpload } from "src/components/DragDropFileUpload";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { useDeleteProductPhoto, useUploadProductPhoto } from "src/api/client-api/products/useProductPhotos";
import { ProductFormType } from "../ProductForm/ProductForm.types";

type ProductPhotoUploadBaseProps = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    disabled?: boolean;
    onPersistencePendingChange?: (pending: boolean) => void;
};

type ProductPhotoUploadProps =
    | (ProductPhotoUploadBaseProps & {
          appearance?: "legacy";
          variant?: "default";
          size?: number;
      })
    | (ProductPhotoUploadBaseProps & {
          appearance: "product-v2";
      } & (
              | {
                    variant?: "default";
                    size?: number;
                }
              | {
                    variant: "compact";
                    size?: never;
                }
              | {
                    variant: "thumbnail";
                    size?: never;
                }
          ));

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const ProductPhotoUpload = (props: ProductPhotoUploadProps) => {
    const { product, setProduct, disabled = false, onPersistencePendingChange } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const isProductV2 = props.appearance === "product-v2";
    const mediaUpload = theme.custom.mediaUpload;
    const isCompact = isProductV2 && props.variant === "compact";
    const isThumbnail = isProductV2 && props.variant === "thumbnail";
    const hasInsetSurface = isCompact || isThumbnail;
    const layout =
        isCompact ? mediaUpload.compact
        : isThumbnail ? mediaUpload.thumbnail
        : mediaUpload.default;
    const resolvedSize =
        isProductV2 ?
            hasInsetSurface ? layout.size
            :   (props.size ?? mediaUpload.default.size)
        :   (props.size ?? theme.spacing(15));
    const emptyCopy = isCompact ? mediaUpload.compact.emptyCopy : mediaUpload.default.emptyCopy;
    const contentInset =
        isCompact ? mediaUpload.compact.contentInset
        : isThumbnail ? mediaUpload.thumbnail.contentInset
        : undefined;
    const boxShadow =
        isCompact ? mediaUpload.compact.boxShadow
        : isThumbnail ? mediaUpload.thumbnail.boxShadow
        : "none";
    const stroke = layout.stroke;
    const uploadProductPhoto = useUploadProductPhoto();
    const deleteProductPhoto = useDeleteProductPhoto();
    const [error, setError] = useState<string | null>(null);
    const isLoading = uploadProductPhoto.isLoading || deleteProductPhoto.isLoading;
    const isInteractionDisabled = disabled || isLoading;

    const pendingPhotos = product.pendingProductPhotos ?? [];
    const existingPhotos = product.photos ?? [];
    const primaryPhoto = existingPhotos.find((photo) => photo.isPrimary) ?? existingPhotos[0] ?? null;
    const primaryPendingPhoto = pendingPhotos[0] ?? null;

    const pendingPreviewUrl = useMemo(() => {
        if (!primaryPendingPhoto) {
            return null;
        }

        return URL.createObjectURL(primaryPendingPhoto);
    }, [primaryPendingPhoto]);

    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) {
                URL.revokeObjectURL(pendingPreviewUrl);
            }
        };
    }, [pendingPreviewUrl]);

    const handleFilesSelected = async (files: File[]) => {
        if (isInteractionDisabled) {
            return;
        }

        setError(null);
        const validFiles = files.filter((file) => {
            return ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE;
        });

        if (validFiles.length !== files.length) {
            setError(t("product.photos.invalid_file", "Only JPG, PNG or WebP images up to 10 MB are supported."));
        }

        const selectedFile = validFiles[0];
        if (!selectedFile) {
            return;
        }

        setProduct((prevState) => ({
            ...prevState,
            pendingProductPhotos: [selectedFile],
        }));

        if (product.id === undefined) {
            return;
        }

        onPersistencePendingChange?.(true);
        try {
            const uploadedPhoto = await uploadProductPhoto.mutateAsync({
                productId: product.id,
                file: selectedFile,
                isPrimary: true,
                replacePhotos: existingPhotos,
            });

            setProduct((prevState) => ({
                ...prevState,
                photos: [uploadedPhoto],
                pendingProductPhotos: [],
            }));
        } catch (uploadError) {
            setProduct((prevState) => ({
                ...prevState,
                pendingProductPhotos: [],
            }));
            throw uploadError;
        } finally {
            onPersistencePendingChange?.(false);
        }
    };

    const handleDeletePending = () => {
        setProduct((prevState) => ({
            ...prevState,
            pendingProductPhotos: [],
        }));
    };

    const handleDeleteCurrent = async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (isInteractionDisabled) {
            return;
        }

        if (primaryPendingPhoto) {
            handleDeletePending();
            return;
        }

        if (product.id === undefined) {
            return;
        }

        onPersistencePendingChange?.(true);
        try {
            await Promise.all(
                existingPhotos.map((photo) => deleteProductPhoto.mutateAsync({ productId: product.id!, photo })),
            );
            setProduct((prevState) => ({
                ...prevState,
                photos: [],
            }));
        } finally {
            onPersistencePendingChange?.(false);
        }
    };

    const previewUrl = primaryPendingPhoto ? pendingPreviewUrl : normalizeLocalS3DisplayUrl(primaryPhoto?.url);
    const previewAlt =
        primaryPendingPhoto?.name ??
        primaryPhoto?.alt ??
        primaryPhoto?.originalName ??
        t("product.photos.preview_alt", "Product photo");
    return (
        <Stack gap={theme.spacing(1)} sx={{ width: "fit-content" }}>
            <DragDropFileUpload
                accept="image/jpeg,image/png,image/webp"
                multiple={false}
                disabled={isInteractionDisabled}
                isUploading={isLoading}
                onFilesSelected={handleFilesSelected}
                sx={{
                    width: resolvedSize,
                    height: resolvedSize,
                    minHeight: resolvedSize,
                    p: isProductV2 ? theme.spacing(0) : 0,
                    position: isProductV2 ? "relative" : undefined,
                    borderRadius: isProductV2 ? mediaUpload.borderRadius : theme.spacing(1),
                    border:
                        isProductV2 ?
                            hasInsetSurface ? "none"
                            :   `${stroke.width} ${stroke.style} ${stroke.color}`
                        :   `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                    boxShadow: isProductV2 ? boxShadow : undefined,
                    backgroundColor: "transparent",
                    overflow: "hidden",
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                {isLoading ?
                    isProductV2 ?
                        <FoodsLoadingIndicator
                            label={t("product.photos.loading", "Uploading product photo")}
                            size={mediaUpload.loadingIndicatorSize}
                        />
                    :   <CircularProgress size={theme.spacing(3.5)} />
                : previewUrl ?
                    <Box
                        sx={{
                            position: hasInsetSurface ? "absolute" : "relative",
                            inset: contentInset,
                            width: hasInsetSurface ? "auto" : "100%",
                            height: hasInsetSurface ? "auto" : "100%",
                            "&:hover .ProductPhotoUpload-delete": {
                                opacity: 1,
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={previewUrl}
                            alt={previewAlt}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                        {!isInteractionDisabled &&
                            (isProductV2 ?
                                <Box
                                    className="ProductPhotoUpload-delete"
                                    sx={{
                                        position: "absolute",
                                        top: theme.spacing(0.5),
                                        right: theme.spacing(0.5),
                                        opacity: 0,
                                        transition: theme.transitions.create("opacity", {
                                            duration: theme.transitions.duration.shortest,
                                        }),
                                        "&:focus-within": {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    <FoodsDeleteButton
                                        ariaLabel={t("product.photos.delete", "Delete product photo")}
                                        onClick={handleDeleteCurrent}
                                        buttonProps={{ type: "button" }}
                                        tooltip={{ text: t("product.photos.delete", "Delete product photo") }}
                                    />
                                </Box>
                            :   <IconButton
                                    size="small"
                                    onClick={handleDeleteCurrent}
                                    aria-label={t("product.photos.delete", "Delete product photo")}
                                    className="ProductPhotoUpload-delete"
                                    sx={{
                                        position: "absolute",
                                        top: theme.spacing(0.5),
                                        right: theme.spacing(0.5),
                                        opacity: 0,
                                        backgroundColor: theme.palette.background.paper,
                                        boxShadow: theme.shadows[1],
                                        transition: theme.transitions.create("opacity", {
                                            duration: theme.transitions.duration.shortest,
                                        }),
                                        "&:focus-visible": {
                                            opacity: 1,
                                        },
                                        "&:hover": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }}
                                >
                                    <DeleteOutline fontSize="small" />
                                </IconButton>)}
                    </Box>
                : isProductV2 ?
                    <FoodsTypography
                        variant={emptyCopy.typographyVariant}
                        sx={{
                            color: theme.custom.grey[2000],
                            textAlign: "center",
                            padding: isCompact ? theme.spacing(0) : theme.spacing(1),
                            lineHeight: isCompact ? undefined : mediaUpload.default.emptyCopy.lineHeight,
                            width: isCompact ? mediaUpload.compact.emptyCopy.width : undefined,
                        }}
                    >
                        {t("product.v2.identity.add_picture", "Add a picture")}
                    </FoodsTypography>
                :   <MuiTypography
                        variant="body-regular"
                        sx={{
                            color: theme.custom.grey[2000],
                            textAlign: "center",
                            padding: theme.spacing(1),
                            lineHeight: 1.3,
                        }}
                    >
                        {t("product.v2.identity.add_picture", "Add a picture")}
                    </MuiTypography>
                }

                {hasInsetSurface && (
                    <Box
                        aria-hidden
                        className="ProductPhotoUpload-stroke"
                        sx={{
                            position: "absolute",
                            inset: theme.spacing(0),
                            borderColor: stroke.color,
                            borderStyle: stroke.style,
                            borderWidth: stroke.width,
                            borderRadius: "inherit",
                            pointerEvents: "none",
                        }}
                    />
                )}
            </DragDropFileUpload>

            {error &&
                (isProductV2 ?
                    <FoodsTypography
                        variant="caption"
                        sx={{ color: theme.palette.error.main, maxWidth: mediaUpload.errorMaxWidth }}
                    >
                        {error}
                    </FoodsTypography>
                :   <MuiTypography variant="caption" color="error" sx={{ maxWidth: theme.spacing(32.5) }}>
                        {error}
                    </MuiTypography>)}
        </Stack>
    );
};

const normalizeLocalS3DisplayUrl = (url?: string): string | undefined => {
    if (!url) {
        return undefined;
    }

    return url.replace("https://api.foodpilot.test/mybucket/", "https://api.foodpilot.test/ui/mybucket/");
};
